import { getAccessToken, getSubId } from '@devhacker/shared/features/auth/utils/token';
import {
  addFavorite,
  addFolder,
  patchFavorite,
  removeFavorite,
} from '@devhacker/shared/features/profile/api';
import { getFullUrl } from '~/utils/index';
import { isAnyRecipePage } from './router';
import { useSystemPush } from '@devhacker/shared/features/system-push/useSystemPush';
import { pushBadge } from '~/components/Pusher/websockets-functions';
import { useArticlesStore } from '~/store/articles';
import { useFoldersStore } from '~/store/folders';
import { useIndexStore } from '~/store';
import { usePageStore } from '~/store/pagesData';

type Post = {
  title: string;
  url: string;
  id: number;
  type?: string;
};

// в данный момент идет работа с избранным
let isManagingFavorites = false;

// избранное гостя — авторизационный пуш вместо редиректа на auth-sso
export function favoriteDeferred() {
  useSystemPush().requestAuthPush();
}

// удалить избранное
export function favoriteDelete(
  favorite: IFavoriteItem,
  post: Post,
  ctx: any,
  sendAnalytics: boolean = true,
  cb: Function = () => {},
): void {
  if (isManagingFavorites) return;
  isManagingFavorites = true;

  const token = getAccessToken();
  if (!token) return;

  const subId = getSubId(token);
  if (!subId) return;

  return removeFavorite(subId, favorite.id)
    .then(() => {
      useArticlesStore().removeFavorite(post.id);
      if (sendAnalytics) {
        sendRemoveFavoriteEvent(post, ctx);
      }
      const title = post ? ` <b>${post.title}</b> ` : ' ';
      const notificationMessage =
        isAnyRecipePage(ctx._route.name) || post.type === 'recipe'
          ? `Рецепт${title}удален из избранного`
          : `Статья${title}удалена из избранного`;
      return pushBadge({ content: notificationMessage });
    })
    .then(() => useFoldersStore().getFolders())
    .then(() => cb())
    .catch((error: any) => {
      const errorMessage = error.data?.msg || 'Что-то пошло не так. Попробуйте позже.';
      return pushBadge({ content: errorMessage, isError: true });
    })
    .finally(() => {
      isManagingFavorites = false;
    });
}

// добавить статью в папку избранного
export function favoriteAddToFolder(
  folder: IFolderItem,
  post: Post,
  sendAnalytics: boolean = true,
  ctx: any,
  isNewFolder: boolean = false,
): any {
  if (isManagingFavorites) return;
  isManagingFavorites = true;

  const token = getAccessToken();
  if (!token) return;

  const subId = getSubId(token);
  if (!subId) return;

  return addFavorite<{ data: IFavoriteItem }>(subId, { post_id: post.id, folder_id: folder.id })
    .then((resp) => {
      useArticlesStore().addFavorite(resp.data);

      if (!isNewFolder && sendAnalytics) {
        sendAddFavoriteEvent(post, ctx);
      }

      const title = post ? ` <b>${post.title}</b> ` : ' ';
      const notificationMessage =
        isAnyRecipePage(ctx._route.name) || post.type === 'recipe'
          ? `Рецепт${title}добавлен в папку <b>${folder.name}</b>`
          : `Статья${title}добавлена в папку <b>${folder.name}</b>`;

      return pushBadge({
        content: notificationMessage,
        clickHandler: () => {
          const userId = useIndexStore().userIntegerId;
          if (userId) {
            location.href = `/profile/user/${userId}/favorites`;
          }
        },
      });
    })
    .then(() => useFoldersStore().getFolders())
    .catch((error: any) => {
      const errorMessage = error.data?.msg || 'Что-то пошло не так. Попробуйте позже.';
      return pushBadge({ content: errorMessage, isError: true });
    })
    .finally(() => {
      isManagingFavorites = false;
    });
}

// добавить папку к избранному и добавить в нее пост
export function favoriteCreateFolderAndAdd(
  folderName: string,
  post: Post,
  ctx: any,
  sendAnalytics: boolean = true,
  cb: Function = () => {},
) {
  if (isManagingFavorites) return;
  isManagingFavorites = true;

  const token = getAccessToken();
  if (!token) return;

  const subId = getSubId(token);
  if (!subId) return;

  return addFolder<{ data: IFolderItem }>(subId, folderName)
    .then((resp) => {
      const { data: folder } = resp;

      isManagingFavorites = false;

      if (sendAnalytics) {
        sendAddFavoriteInNewFolderEvent(post, ctx);
      }

      favoriteAddToFolder(folder, post, false, ctx, true).then(cb);
    })
    .catch((error: any) => {
      const errorMessage = error.data?.msg || 'Что-то пошло не так. Попробуйте позже.';
      return pushBadge({ content: errorMessage, isError: true });
    });
}

// добавить папку к избранному и перенести в нее пост
export function favoriteCreateFolderAndTransfer(
  folderName: string,
  favorite: IFavoriteItem,
  post: Post,
  _: void,
  ctx: any,
  cb: Function = () => {},
) {
  if (isManagingFavorites) return;
  isManagingFavorites = true;

  const token = getAccessToken();
  if (!token) return;

  const subId = getSubId(token);
  if (!subId) return;

  return addFolder<{ data: IFolderItem }>(subId, folderName)
    .then((resp) => {
      const { data: folder } = resp;

      isManagingFavorites = false;
      favoriteTransferToFolder(favorite, folder, post, undefined, ctx).then(cb);
    })
    .catch((error: any) => {
      const errorMessage = error.data?.msg || 'Что-то пошло не так. Попробуйте позже.';
      return pushBadge({ content: errorMessage, isError: true });
    });
}

// перенести избранное из одной папки в другую
export function favoriteTransferToFolder(
  favorite: IFavoriteItem,
  folder: IFolderItem,
  post: Post,
  _: void,
  ctx: any,
  cb: Function = () => {},
): any {
  if (isManagingFavorites) return;
  isManagingFavorites = true;

  const token = getAccessToken();
  if (!token) return;

  const subId = getSubId(token);
  if (!subId) return;

  const articlesStore = useArticlesStore();

  return patchFavorite<{ data: IFavoriteItem }>(subId, favorite.id, folder.id)
    .then((resp) => {
      const { data: favorite } = resp;
      cb();

      articlesStore.removeFavorite(favorite.post_id);
      articlesStore.addFavorite(favorite);

      const title = post ? ` <b>${post.title}</b> ` : ' ';
      const notificationMessage =
        isAnyRecipePage(ctx._route.name) || post.type === 'recipe'
          ? `Рецепт${title}перемещен в папку <b>${folder.name}</b>`
          : `Статья${title}перемещена в папку <b>${folder.name}</b>`;

      return pushBadge({
        content: notificationMessage,
        clickHandler: () => {
          const userId = useIndexStore().userIntegerId;
          if (userId) {
            location.href = `/profile/user/${userId}/favorites`;
          }
        },
      });
    })
    .then(() => useFoldersStore().getFolders())
    .catch((error: any) => {
      const errorMessage = error.data?.msg || 'Что-то пошло не так. Попробуйте позже.';
      return pushBadge({ content: errorMessage, isError: true });
    })
    .finally(() => {
      isManagingFavorites = false;
    });
}

// ------------------------
// аналитика для избранного
// ------------------------
const sendAddFavoriteEvent = (post: Post, ctx: any): void => {
  ctx.$sendYandexMetrika({
    level1: 'Избранное_добавление избранного',
    level4: getFullUrl(post.url),
    // @ts-ignore
    level6: usePageStore().currentTitle ?? 'unknown',
  });
};

const sendRemoveFavoriteEvent = (post: Post, ctx: any): void => {
  ctx.$sendYandexMetrika({
    level1: 'Избранное_удаление_избранного',
    level4: getFullUrl(post.url),
    // @ts-ignore
    level6: usePageStore().currentTitle ?? 'unknown',
  });
};

const sendAddFavoriteInNewFolderEvent = (post: Post, ctx: any): void => {
  ctx.$sendYandexMetrika({
    level1: 'Избранное_добавление избранного',
    level4: getFullUrl(post.url),
    // @ts-ignore
    level6: usePageStore().currentTitle ?? 'unknown',
  });
};
